// Astra · el idioma de la página, sacado de la dirección.
//
// Petición del servidor `PETICION_TRADUCCIONES_WEB.md` (04.11.26) y propuesta
// `PROPUESTA_TRADUCCIONES_WEB.md` en `astra-fm/temp`, aprobada por Pablo.
//
// 🔴 **El idioma va en el prefijo de la dirección**: `/en/…`, `/ca/…`, `/fr/…`,
// y el español sin prefijo. Es lo que necesitan los buscadores para indexar cada
// idioma por separado.
//
// 🔴 **El prefijo es la base del historial, no una ruta.** El router se crea con
// `createWebHistory("/en/")` y las rutas no cambian: `route.path` sigue siendo
// `/radar` y los `RouterLink` salen ya como `/en/radar`. Por eso cambiar de
// idioma **recarga la página** (`location.assign`): la base no se cambia en
// caliente.
//
// ⚠️ Las direcciones se quedan en español también con prefijo
// (`/fr/actualidad/<slug>`). Los PHP de Open Graph, el sitemap y el 301 de nginx
// no tienen que aprender traducciones.

export const IDIOMAS = ["es", "en", "ca", "fr"];

// El de referencia: va sin prefijo y es el que manda si una traducción falta.
export const IDIOMA_BASE = "es";

const CON_PREFIJO = IDIOMAS.filter((i) => i !== IDIOMA_BASE);
const PREFIJO = new RegExp(`^/(${CON_PREFIJO.join("|")})(?=/|$)`);

/** El idioma de una dirección: `/en/radar` → `en`, `/radar` → `es`. */
export function idiomaDe(pathname = "") {
  const m = String(pathname).match(PREFIJO);
  return m ? m[1] : IDIOMA_BASE;
}

/** La dirección sin el prefijo de idioma: `/en/radar` → `/radar`. */
export function sinIdioma(pathname = "") {
  return String(pathname).replace(PREFIJO, "") || "/";
}

/** La misma dirección en otro idioma: `('/radar', 'fr')` → `/fr/radar`. */
export function enIdioma(ruta, idioma) {
  const limpia = sinIdioma(ruta);
  if (idioma === IDIOMA_BASE) return limpia;
  return limpia === "/" ? `/${idioma}/` : `/${idioma}${limpia}`;
}

// El idioma de esta carga. No cambia mientras la página esté abierta.
export const IDIOMA =
  typeof window === "undefined" ? IDIOMA_BASE : idiomaDe(window.location.pathname);

/** La base del historial para `createWebHistory`: `/` o `/en/`. */
export const BASE_HISTORIAL = IDIOMA === IDIOMA_BASE ? "/" : `/${IDIOMA}/`;

// ═══════════════════════════════════════════════════════════════════════════
// Publicar los idiomas (tanda 5)
// ═══════════════════════════════════════════════════════════════════════════
//
// 🔴 **El interruptor.** Con `false`, `/en`, `/ca` y `/fr` llevan `noindex`, no
// hay `hreflang` y nadie llega a ellas solo. Con `true` se encienden juntas la
// detección del idioma, los `hreflang` y el fin del `noindex`. El sitemap tiene
// el suyo (`IDIOMAS_PUBLICOS` en `public/sitemap.php`): se cambian los dos en el
// mismo commit.
//
// ⚠️ No se enciende sin el selector de idioma: quien llega redirigido por su
// navegador y prefiere otro idioma tiene que poder cambiarlo.
export const IDIOMAS_PUBLICOS = false;

// La elección de cada navegador: la del selector o la que detectó la primera
// visita. Mientras exista, no se vuelve a detectar.
const LLAVE_IDIOMA = "astra_idioma";

// De dónde salió el idioma de la carga que viene: `"deteccion"` o `"selector"`.
// Lo escribe quien recarga y lo lee `main.js` en la página nueva para contarlo.
export const LLAVE_IDIOMA_DESDE = "astra_idioma_desde";

// Cada idioma se nombra en su lengua, nunca traducido («Français», no
// «Francés»): es lo que busca quien no entiende la página que tiene delante.
export const NOMBRE_DEL_IDIOMA = { es: "Español", en: "English", ca: "Català", fr: "Français" };

export function idiomaGuardado() {
  try {
    const i = localStorage.getItem(LLAVE_IDIOMA);
    return IDIOMAS.includes(i) ? i : null;
  } catch (e) {
    return null;
  }
}

export function guardaIdioma(idioma) {
  try {
    if (IDIOMAS.includes(idioma)) localStorage.setItem(LLAVE_IDIOMA, idioma);
  } catch (e) {
    // Sin almacenamiento se detecta en cada visita, y no pasa nada más.
  }
}

/** El primero de los idiomas del navegador que tenemos: `["en-GB", "es"]` → `en`. */
export function idiomaDelNavegador(lista = navigator.languages || [navigator.language]) {
  for (const l of lista || []) {
    const base = String(l || "").toLowerCase().split("-")[0];
    if (IDIOMAS.includes(base)) return base;
  }
  return null;
}

// 🔴 **Los buscadores nunca se redirigen.** Googlebot renderiza con el navegador
// en `en-US`: si se le mandara a `/en/`, la versión española no se indexaría
// nunca. Cada idioma lo descubren por los `hreflang` y el sitemap.
const ROBOT = /bot|crawl|spider|slurp|facebookexternalhit|embedly|preview|lighthouse|headless/i;

/**
 * A dónde mandar la primera visita, o `null` para quedarse.
 *
 * Decisión de Pablo (04.11.26): **se detecta el idioma y se redirige**, sin
 * aviso. Solo cuando se entra por una dirección sin prefijo (la española): un
 * enlace a `/en/…` ya dice qué idioma quiere quien lo comparte. Y solo una vez:
 * lo detectado se guarda, y a partir de ahí manda lo guardado, que es también lo
 * que escribe el selector.
 */
export function destinoDeLaVisita() {
  if (!IDIOMAS_PUBLICOS || typeof window === "undefined") return null;
  if (IDIOMA !== IDIOMA_BASE) return null;
  if (navigator.webdriver || ROBOT.test(navigator.userAgent || "")) return null;

  let elegido = idiomaGuardado();
  if (!elegido) {
    elegido = idiomaDelNavegador() || IDIOMA_BASE;
    guardaIdioma(elegido);
  }
  if (elegido === IDIOMA_BASE) return null;
  const { pathname, search, hash } = window.location;
  return enIdioma(pathname, elegido) + search + hash;
}

/**
 * El selector: guarda la elección y recarga la misma dirección en otro idioma
 * (`/radar` → `/fr/radar`). Recarga porque el prefijo es la base del historial
 * (ver arriba). Elegir el idioma actual no hace nada.
 */
export function cambiaIdioma(idioma) {
  if (!IDIOMAS.includes(idioma) || idioma === IDIOMA) return;
  guardaIdioma(idioma);
  try {
    sessionStorage.setItem(LLAVE_IDIOMA_DESDE, "selector");
  } catch (e) {
    // Sin almacenamiento, sin evento.
  }
  const { pathname, search, hash } = window.location;
  window.location.assign(enIdioma(pathname, idioma) + search + hash);
}
